import React, { useState } from "react"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { AlertTriangle, RotateCcw, X, Trash2, AlertCircle, Info, Users } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Separator } from "@/components/ui/separator"
import { useTournamentCancel } from "@/graphql/hooks/useTournamentCancel"
import { useTournamentFinish } from "@/graphql/hooks/useTournamentFinish"
import { useRoundReset } from "@/graphql/hooks/useRoundReset"
import { useTournamentRounds } from "@/graphql/hooks/useTournamentRounds"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "@/hooks/use-toast"
import { useAuthContext } from "@/contexts/AuthContext"
import { Tournament, useTournamentInfo } from "@/graphql"
import Layout from "@/components/Layout/Layout"
import { HeaderTournament, TournamentNavigation } from "@/components/TournamentNavigation"
import NotFound from "./NotFound"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { TournamentStatusBadge } from "@/components/ui/TournamentStatusBadge"

interface TournamentDangerZoneProps {
  tournament: Tournament
  canManageTournament: boolean
  isAdmin: boolean
}

const roundStatus = {
  1: "En curso",
  2: "Terminada",
  3: "Anulada",
}

export const TournamentAdminView = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>()
  const { userInfo, loading: authLoading, isAdmin } = useAuthContext()
  const { tournament, loading, error } = useTournamentInfo(Number(tournamentId))

  // Check authorization
  const canAccess = () => {
    if (authLoading || !userInfo || !tournament) return false

    // Check if user is admin (we'll assume admins have categoryId 1 for now)
    if (isAdmin) {
      return true
    }

    // Check if user owns the store that owns this tournament
    const isStoreOwner = userInfo.User?.Person?.Store?.id === tournament.storeId

    return isAdmin || isStoreOwner
  }

  if (loading) {
    return <LoadingScreen message="Cargando administración del torneo..." />
  }

  if (error || !tournament || !canAccess()) {
    return <NotFound />
  }

  return (
    <Layout>
      <HeaderTournament tournament={tournament} />
      <TournamentNavigation
        tournament={tournament}
        tournamentName={tournament?.name || ""}
        tournamentStatusId={tournament.statusId}
        currentPage="admin"
      />
      <TournamentDangerZoneData
        tournament={tournament}
        canManageTournament={canAccess()}
        isAdmin={isAdmin}
      />
    </Layout>
  )
}

export const TournamentDangerZoneData: React.FC<TournamentDangerZoneProps> = ({
  tournament,
  canManageTournament,
  isAdmin,
}) => {
  const navigate = useNavigate()
  const { tournamentCancel, tournamentDelete, loadingCancel, loadingDelete } = useTournamentCancel()
  const { tournamentFinish, loading: finishLoading } = useTournamentFinish()
  const { roundReset, loading: resetLoading } = useRoundReset()
  const { rounds, refetch } = useTournamentRounds(tournament?.id, canManageTournament)
  const [confirmText, setConfirmText] = useState("")

  if (!canManageTournament) {
    return null
  }

  const handleRoundReset = async (roundId: number, roundName: string) => {
    try {
      await roundReset({
        tournamentId: tournament.id,
        roundId: roundId,
        resetType: 1, // Reset type 1 for standard reset
      })
      toast({
        title: "Ronda reiniciada",
        variant: "success",
        description: `La ronda "${roundName}" ha sido reiniciada exitosamente.`,
      })
      refetch()
    } catch (error) {
      console.error("Error resetting round:", error)
      toast({
        title: "Error al reiniciar ronda",
        description: "No se pudo reiniciar la ronda. Inténtalo de nuevo.",
        variant: "destructive",
      })
    }
  }

  const handleCancelTournament = async () => {
    try {
      await tournamentCancel({ tournamentId: tournament.id })
      window.location.reload()
    } catch (error) {
      console.error("Error cancelling tournament:", error)
    }
  }

  const handleDeleteTournament = async () => {
    try {
      await tournamentDelete({ tournamentId: tournament.id })
      navigate("/tournaments")
    } catch (error) {
      console.error("Error deleting tournament:", error)
    }
  }

  const handleFinishTournament = async () => {
    try {
      await tournamentFinish({ tournamentId: tournament.id })
      window.location.reload()
    } catch (error) {
      console.error("Error finishing tournament:", error)
    }
  }

  // Filter rounds that can be reset (only rounds in progress)
  const resettableRounds = rounds?.filter((round) => round.statusId === 1) || []
  const [lastRound] = rounds

  // Check tournament status for different actions
  const canFinishTournament =
    tournament.statusId === 5 &&
    canManageTournament &&
    lastRound &&
    Number(lastRound.totalMatchPendings) === 0
  const canCancelTournament = [6].includes(tournament.statusId) && canManageTournament
  const canDeleteTournament = isAdmin

  return (
    <Card className="border-destructive/20 bg-destructive/5">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <CardTitle className="text-destructive">DANGER ZONE</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Las siguientes acciones son irreversibles. Procede con cautela.
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Round Management Section */}
        {resettableRounds.length > 0 && (
          <div className="space-y-4">
            <div>
              <h4 className="font-medium flex items-center gap-2 mb-3">
                <RotateCcw className="h-4 w-4" />
                Gestión de Rondas
              </h4>
              <div className="space-y-3">
                {resettableRounds.map((round) => (
                  <div
                    key={round.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-background"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium">{round.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant={round.statusId === 1 ? "secondary" : "default"}
                            className="text-xs"
                          >
                            {roundStatus[round.statusId] || "N/A"}
                          </Badge>
                          {round.totalMatches && (
                            <Badge variant="outline" className="text-xs">
                              Mesas: {round.totalMatches}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="gap-2 flex">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={resetLoading}
                        className="border-green-500 text-green-700 hover:bg-green-500"
                        onClick={() => navigate(`${round.urlView}/${tournament.id}`)}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        {"Ver Ronda"}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={resetLoading}
                            className="border-orange-500 text-orange-700 hover:bg-orange-500"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            {resetLoading ? "Borrando..." : "Eliminar Ronda"}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                              <AlertCircle className="h-5 w-5 text-orange-500" />
                              ¿Eliminar ronda?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta acción reiniciará la ronda "{round.name}" eliminando todos los
                              emparejamientos y resultados actuales. Los jugadores permanecerán
                              registrados en el torneo.
                              <br />
                              <br />
                              <strong>Esta acción no se puede deshacer.</strong>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRoundReset(round.id, round.name)}
                              className="bg-orange-500 text-white hover:bg-orange-600"
                            >
                              Sí, reiniciar ronda
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Separator />
          </div>
        )}

        {/* Tournament Management Section */}
        <div className="space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <Trash2 className="h-4 w-4" />
            Gestión del Torneo
          </h4>

          <div className="grid gap-4">
            {/* Finish Tournament */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border rounded-lg bg-background">
              <div>
                <p className="font-medium">Finalizar Torneo</p>
                <p className="text-sm text-muted-foreground">
                  Se encarga de reportar el evento, concluyendo las rondas y enviando los resultados
                  al ranking oficial.
                </p>
                {!canFinishTournament && (
                  <div>
                    <p className="text-sm text-destructive">
                      <Info className="h-3 w-3 inline mr-1" />
                      Solo se puede reportar si es que todos los resultados de la última ronda ya
                      fueron asignados. Y además, el estado del torneo es{" "}
                    </p>
                    <TournamentStatusBadge statusId={5} className="inline" />
                  </div>
                )}
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={finishLoading || !canFinishTournament}
                    className="border-blue-500 text-blue-700 hover:bg-blue-500/80"
                  >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {finishLoading ? "Reportando..." : "Reportar"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Finalizar Torneo?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción finalizará el torneo "{tournament.name}" y enviará los resultados
                      al ranking oficial. Una vez finalizado, no se podrán hacer más cambios.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleFinishTournament}
                      className="bg-blue-500 text-white hover:bg-blue-600"
                    >
                      Sí, reportar torneo
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {/* Cancel Tournament */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border rounded-lg bg-background">
              <div>
                <p className="font-medium">Cancelar Torneo</p>
                <p className="text-sm text-muted-foreground">
                  Cancela el torneo, volviendo al estado <TournamentStatusBadge statusId={4} />{" "}
                  liberando la última ronda permitiendo cambios en emparejamientos y resultados.
                </p>
                <p className="text-sm text-destructive">
                  <Info className="h-3 w-3 inline mr-1" />
                  Solo se puede cancelar un torneo que esté <TournamentStatusBadge statusId={6} />
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={loadingCancel || !canCancelTournament}
                    className="border-orange-500 text-orange-700 hover:bg-orange-500"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {loadingCancel ? "Cancelando..." : "Cancelar"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      ¿Cancelar Torneo?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción cancelará el torneo "{tournament.name}".
                      <br />
                      <br />
                      <strong>Esta acción no se puede deshacer.</strong>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>No, mantener torneo</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCancelTournament}
                      className="bg-orange-500 text-white hover:bg-orange-600"
                    >
                      Sí, cancelar torneo
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {/* Delete Tournament */}
            {canDeleteTournament && (
              <div className="flex items-center justify-between p-4 border border-destructive/20 rounded-lg bg-destructive/5">
                <div>
                  <p className="font-medium text-destructive">Eliminar Torneo</p>
                  <p className="text-sm text-muted-foreground">
                    Elimina completamente el torneo y todos sus datos asociados.
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={loadingDelete}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      {loadingDelete ? "Eliminando..." : "Eliminar"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                        ¿Eliminar Torneo Permanentemente?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta acción eliminará completamente el torneo "{tournament.name}" y todos
                        sus datos asociados, incluyendo:
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Todas las rondas y resultados</li>
                          <li>Registros de jugadores</li>
                          <li>Estadísticas del torneo</li>
                        </ul>
                        <br />
                        <strong className="text-destructive">
                          Esta acción es irreversible y no se puede deshacer.
                        </strong>
                        {/* Input de confirmación */}
                        <div className="mt-4">
                          <label className="text-sm font-medium">
                            Escribe <span className="font-bold">ELIMINAR</span> para confirmar
                          </label>
                          <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="Escribe ELIMINAR"
                            className="mt-2 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>No, mantener torneo</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteTournament}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={confirmText !== "ELIMINAR" || finishLoading}
                      >
                        Sí, eliminar permanentemente
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
